<template>
    <AppHead title="Checkout | 10xTravel+" />
    <div class="min-h-screen bg-slate-50 dark:bg-slate-900/40">
        <!-- Header -->
        <div
            class="max-w-4xl mx-auto px-4 md:px-6 py-4 md:py-6 flex items-center justify-between"
        >
            <Link
                :href="backHref"
                class="flex items-center gap-1 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:text-slate-100 transition-colors"
            >
                <Icon icon="chevron-left" class="w-4 h-4" />
                Back
            </Link>
            <div class="flex items-center">
                <img src="/images/logo.svg" alt="MY10x" class="h-7" />
            </div>
            <div
                class="hidden md:flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300"
            >
                <Icon icon="user" class="w-5 h-5" />
                <span>{{ userName }}</span>
            </div>
        </div>

        <!-- Main Content -->
        <div class="max-w-4xl mx-auto px-4 md:px-6 pb-10 md:pb-16">
            <div class="flex flex-col-reverse md:flex-row gap-6">
                <!-- Left: Checkout Form -->
                <div
                    class="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 md:p-8"
                >
                    <h1 class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-6">
                        Checkout
                    </h1>

                    <!-- Error Message -->
                    <div
                        v-if="errorMessage"
                        class="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6"
                    >
                        <svg
                            class="w-5 h-5 text-red-500 shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <p class="text-sm text-red-700">{{ errorMessage }}</p>
                    </div>

                    <!-- Stripe Payment Element -->
                    <div id="payment-element" class="mb-6"></div>

                    <!-- Loading state while Stripe loads -->
                    <div
                        v-if="!stripeReady"
                        class="flex items-center justify-center py-12"
                    >
                        <div
                            class="animate-spin rounded-full h-8 w-8 border-b-2 border-orange"
                        ></div>
                    </div>

                    <!-- Pay Button -->
                    <button
                        v-if="stripeReady"
                        type="button"
                        class="w-full bg-orange text-white text-sm font-medium py-3 px-4 rounded shadow-sm hover:bg-orange-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        :disabled="processing"
                        @click="handleSubmit"
                    >
                        <span
                            v-if="processing"
                            class="flex items-center justify-center gap-2"
                        >
                            <div
                                class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"
                            ></div>
                            Processing...
                        </span>
                        <span v-else>Pay {{ formattedTotal }}</span>
                    </button>

                    <!-- Terms -->
                    <p
                        class="text-xs text-slate-400 text-center mt-4 leading-relaxed"
                    >
                        By confirming your subscription, you allow 10xTravel to
                        charge you for future payments in accordance with their
                        terms. You can always cancel your subscription.
                    </p>

                    <!-- Powered by Stripe -->
                    <div
                        class="flex items-center justify-center gap-3 mt-4 text-xs text-slate-400"
                    >
                        <span>
                            Powered by
                            <strong class="text-slate-500 dark:text-slate-400">stripe</strong>
                        </span>
                        <span class="text-slate-300">|</span>
                        <a
                            href="https://stripe.com/privacy"
                            target="_blank"
                            class="hover:text-slate-500 dark:text-slate-400"
                        >
                            Privacy
                        </a>
                        <span class="text-slate-300">|</span>
                        <a
                            href="https://stripe.com/legal/end-users"
                            target="_blank"
                            class="hover:text-slate-500 dark:text-slate-400"
                        >
                            Terms
                        </a>
                    </div>
                </div>

                <!-- Right: Order Summary -->
                <div
                    class="w-full md:w-80 lg:w-96 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 md:p-8 h-fit"
                >
                    <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100 mb-6">
                        Order summary
                    </h2>

                    <!-- Billing Toggle -->
                    <div class="bg-slate-100 dark:bg-slate-700 rounded p-0.5 flex mb-6">
                        <button
                            type="button"
                            class="flex-1 text-sm font-medium py-2 px-4 rounded transition-all"
                            :class="
                                billingInterval === 'month'
                                    ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm border border-slate-200 dark:border-slate-700'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200'
                            "
                            @click="setBillingInterval('month')"
                        >
                            Monthly
                        </button>
                        <button
                            type="button"
                            class="flex-1 text-sm font-medium py-2 px-4 rounded transition-all flex items-center justify-center gap-1.5"
                            :class="
                                billingInterval === 'year'
                                    ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 shadow-sm border border-slate-200 dark:border-slate-700'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200'
                            "
                            @click="setBillingInterval('year')"
                        >
                            Yearly
                            <span
                                class="text-orange text-xs font-medium whitespace-nowrap"
                            >
                                2 Months Free
                            </span>
                        </button>
                    </div>

                    <!-- Membership Info -->
                    <div class="flex items-start justify-between mb-6">
                        <div>
                            <p class="text-base font-medium text-slate-800 dark:text-slate-100">
                                10xTravel+ membership
                            </p>
                            <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                Billed
                                {{
                                    billingInterval === 'month'
                                        ? 'monthly'
                                        : 'yearly'
                                }}
                            </p>
                        </div>
                        <p class="text-base font-medium text-slate-800 dark:text-slate-100">
                            {{ formattedPrice }}
                        </p>
                    </div>

                    <div class="h-px bg-slate-200 dark:bg-slate-700"></div>

                    <!-- Subtotal -->
                    <div class="flex items-center justify-between mt-6">
                        <p class="text-sm text-slate-500 dark:text-slate-400">Subtotal</p>
                        <p class="text-sm text-slate-800 dark:text-slate-100">
                            {{ formattedPrice }}
                        </p>
                    </div>

                    <!-- Discount -->
                    <div
                        v-if="appliedPromo"
                        class="flex items-center justify-between mt-4"
                    >
                        <div class="flex items-center gap-2">
                            <p class="text-sm text-green-600">Discount</p>
                            <button
                                type="button"
                                class="text-xs text-slate-400 hover:text-red-500"
                                @click="removePromo"
                            >
                                Remove
                            </button>
                        </div>
                        <p class="text-sm text-green-600">
                            -{{ formattedDiscount }}
                        </p>
                    </div>

                    <!-- Tax -->
                    <div class="flex items-center justify-between mt-4">
                        <p class="text-sm text-slate-500 dark:text-slate-400">Tax</p>
                        <p class="text-sm text-slate-800 dark:text-slate-100">$0.00</p>
                    </div>

                    <!-- Total -->
                    <div class="flex items-center justify-between mt-4 mb-6">
                        <p class="text-sm font-medium text-slate-800 dark:text-slate-100">
                            Total for today
                        </p>
                        <p class="text-base font-medium text-slate-800 dark:text-slate-100">
                            {{ formattedTotal }}
                        </p>
                    </div>

                    <div class="h-px bg-slate-200 dark:bg-slate-700"></div>

                    <!-- Promo Code -->
                    <div class="flex gap-2 mt-6">
                        <input
                            v-model="promoCode"
                            type="text"
                            placeholder="Add promo code"
                            class="flex-1 text-sm border border-slate-200 dark:border-slate-700 rounded px-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                            :disabled="!!appliedPromo"
                            @keyup.enter="applyPromoCode"
                        />
                        <button
                            type="button"
                            class="text-sm font-medium text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors shadow-sm disabled:opacity-50"
                            :disabled="
                                !promoCode || promoValidating || !!appliedPromo
                            "
                            @click="applyPromoCode"
                        >
                            {{ promoValidating ? '...' : 'Apply' }}
                        </button>
                    </div>
                    <p v-if="promoError" class="text-xs text-red-500 mt-1">
                        {{ promoError }}
                    </p>

                    <!-- Benefits -->
                    <div class="flex flex-col gap-2 mt-6">
                        <div class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-orange shrink-0"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                            >
                                <path
                                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-.722-.064-1.43-.182-2.016z"
                                />
                            </svg>
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                14-day money back guarantee
                            </p>
                        </div>
                        <div class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-orange shrink-0"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                            >
                                <path
                                    d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                                />
                            </svg>
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                Instant access to all 10xTravel+ features
                            </p>
                        </div>
                        <div class="flex items-center gap-3">
                            <svg
                                class="w-5 h-5 text-orange shrink-0"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="1.5"
                            >
                                <path
                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                />
                            </svg>
                            <p class="text-sm text-slate-500 dark:text-slate-400">
                                Safe &amp; Secure checkout
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import AppHead from '@/Partials/AppHead.vue';
import Icon from '@/components/ui/icon/icon.vue';
import Plain from '@/Layouts/Plain.vue';

defineOptions({ layout: Plain });

const props = defineProps({
    products: {
        type: Array,
        default: () => [],
    },
    stripeKey: {
        type: String,
        default: '',
    },
    fromOnboarding: {
        type: Boolean,
        default: false,
    },
});

const backHref = computed(() => (props.fromOnboarding ? '/quiz' : '/10xtravel-plus/upgrade'));

const page = usePage();
const userName = computed(() => page.props.userData?.name || '');

// Stripe state
let stripe = null;
let elements = null;
let paymentElement = null;
const stripeReady = ref(false);
const processing = ref(false);
const errorMessage = ref('');

// Billing state
const billingInterval = ref('month');
const promoCode = ref('');
const promoError = ref('');
const promoValidating = ref(false);
const appliedPromo = ref(null);

// Computed product based on billing interval
const selectedProduct = computed(() => {
    return (
        props.products.find(
            (p) => p.billing_interval === billingInterval.value
        ) ||
        props.products[0] ||
        null
    );
});

const currentPrice = computed(() => {
    return selectedProduct.value ? parseFloat(selectedProduct.value.price) : 0;
});

const discountAmount = computed(() => {
    if (!appliedPromo.value) return 0;
    if (appliedPromo.value.discount.type === 'percent') {
        return currentPrice.value * (appliedPromo.value.discount.value / 100);
    }
    return appliedPromo.value.discount.value;
});

const totalPrice = computed(() => {
    return Math.max(0, currentPrice.value - discountAmount.value);
});

const formattedPrice = computed(() => {
    return '$' + currentPrice.value.toFixed(2);
});

const formattedDiscount = computed(() => {
    return '$' + discountAmount.value.toFixed(2);
});

const formattedTotal = computed(() => {
    return '$' + totalPrice.value.toFixed(2);
});

const setBillingInterval = (interval) => {
    billingInterval.value = interval;
    // Clear promo when switching intervals
    if (appliedPromo.value) {
        removePromo();
    }
};

const removePromo = () => {
    appliedPromo.value = null;
    promoCode.value = '';
    promoError.value = '';
};

const applyPromoCode = async () => {
    if (!promoCode.value || promoValidating.value) return;

    promoValidating.value = true;
    promoError.value = '';

    try {
        const response = await axios.post('/checkout/validate-promo', {
            promo_code: promoCode.value,
            price_id: selectedProduct.value?.stripe_price_id,
        });

        appliedPromo.value = {
            code: promoCode.value,
            promo_code_id: response.data.promo_code_id,
            discount: response.data.discount,
        };
    } catch (e) {
        promoError.value = e.response?.data?.message || 'Invalid promo code.';
    } finally {
        promoValidating.value = false;
    }
};

const initializeStripe = async () => {
    try {
        // Get setup intent from server
        const { data } = await axios.post('/checkout/setup-intent');

        stripe = Stripe(props.stripeKey);

        elements = stripe.elements({
            clientSecret: data.clientSecret,
            appearance: {
                theme: 'stripe',
                variables: {
                    colorPrimary: '#fd421d',
                    colorBackground: '#ffffff',
                    colorText: '#1e293b',
                    colorDanger: '#ef4444',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '4px',
                },
                rules: {
                    '.Input': {
                        border: '1px solid #e2e8f0',
                        boxShadow: 'none',
                        padding: '10px 16px',
                    },
                    '.Input:focus': {
                        border: '1px solid #fd421d',
                        boxShadow: '0 0 0 1px #fd421d',
                    },
                    '.Label': {
                        fontWeight: '500',
                        fontSize: '14px',
                        marginBottom: '8px',
                    },
                },
            },
        });

        paymentElement = elements.create('payment', {
            layout: 'tabs',
            defaultValues: {
                billingDetails: {
                    email: page.props.userData?.email || '',
                },
            },
            fields: {
                billingDetails: {
                    name: 'auto',
                    email: 'never',
                    address: {
                        country: 'auto',
                        postalCode: 'auto',
                    },
                },
            },
        });

        paymentElement.mount('#payment-element');

        paymentElement.on('ready', () => {
            stripeReady.value = true;
        });
    } catch (e) {
        errorMessage.value =
            'Failed to initialize payment form. Please refresh and try again.';
        console.error('Stripe init error:', e);
    }
};

const handleSubmit = async () => {
    if (processing.value || !stripe || !elements) return;

    processing.value = true;
    errorMessage.value = '';

    try {
        // Confirm the setup intent
        const { setupIntent, error } = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
            confirmParams: {
                payment_method_data: {
                    billing_details: {
                        email: page.props.userData?.email || '',
                    },
                },
            },
        });

        if (error) {
            errorMessage.value = error.message;
            processing.value = false;
            return;
        }

        // Create the subscription on the server
        const response = await axios.post('/checkout/subscribe', {
            payment_method: setupIntent.payment_method,
            price_id: selectedProduct.value?.stripe_price_id,
            promo_code: appliedPromo.value?.promo_code_id || null,
        });

        if (response.data.success) {
            window.location.href = response.data.redirect;
        }
    } catch (e) {
        errorMessage.value =
            e.response?.data?.error ||
            'Something went wrong. Please try again.';
        processing.value = false;
    }
};

onMounted(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (billing === 'year' || billing === 'month') {
        billingInterval.value = billing;
    }

    // Set when the member comes back from Cashier's 3D Secure page without
    // completing the payment (SUPPORT-833).
    if (page.props.flash?.error) {
        errorMessage.value = page.props.flash.error;
    }

    initializeStripe();
});
</script>
